<template>
    <div :id="rootElementId" :style="`height:${SVG_RENDER_HEIGHT}px`"></div>
</template>

<script>

import { getDataUrl, getNeutralColors, isWhite } from '../../utils/functions';
import { loadFonts,copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import Dictionary from '../../dictionary';

  export default {
    name: 'XGChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      shots: {
        // Shots [{"id":1,"x":75.2,"y":67.4,"xG":0.04706717555467873,"min":2,"sec":0,"game":"Valencia - Atletico Madrid","playerId":9,"player":"Jose Gaya","type":"SAVED SHOT","teamName":"Valencia","strategy":"Regular play","bodyPart":"Left footed","homeTeam":true,"penaltyGoal":false,"ownGoal":false,"blockedByOutfielderPlayer":true}]
        type: Array,
        required: true,
      },
      game: {
        // Game like {"gameId":1,"leagueCountryCode":"es","leagueName":"La Liga 2016","matchDate":"02/10/2016","stadiumName":"Mestalla","matchDay":7,"homeTeamId":2,"homeTeamName":"Valencia","homeTeamScore":0,"homeTeamFormation":"4231","homeTeamCoachName":"Salvador González","homeTeamCoachCountryCode":null,"awayTeamId":1,"awayTeamName":"Atletico Madrid","awayTeamScore":2,"awayTeamFormation":"442","awayTeamCoachName":"Diego Simeone","awayTeamCoachCountryCode":"ar","homeTeamGameColor":null,"homeTeamGameSecondColor":null,"awayTeamGameColor":null,"awayTeamGameSecondColor":null,"homeTeamColor":"0xFFFFFF","homeTeamSecondColor":"0x000000","awayTeamColor":"0xFF0000","awayTeamSecondColor":"0xFFFFFF"}
        type: Object,
        required: false
      },
      playerSeasonData: {
        type: Array,
        required: false
      },
      circlesAndBoxes: {
        type: Array,
        required: false
      },
      playerName: {
        type: String,
        required: false
      },
      playerPosition: {
        type: Number,
        required: false
      },
      language: {
        type: String,
        required: true
      },
      homeFillColor:{
        type: String,
        default: "#ffffff"
      },
      homeBorderColor:{
        type: String,
        default: "#000000"
      },
      awayFillColor:{
        type: String,
        default: "#ffffff"
      },
      awayBorderColor:{
        type: String,
        default: "#000000"
      },
      mediaMode:{
        type: Boolean,
        default: false
      },
      postGameAnalysis:{
        type: Boolean,
        default: false
      },
      xGOnTarget:{
        type: Boolean,
        default: false
      },
      finishingXGOT:{
        type: Boolean,
        default: false
      },
      homeTeamName:{
        type: String,
        default: ""
      },
      awayTeamName:{
        type: String,
        default: ""
      },
      team:{
        type: Boolean,
        default: false
      },
      teamOrOpponent: { // At team level we can also show opponent shots: This will be true if showing team and false is showing opponent (Texts change if showing opponent stats)
        type: Boolean,
        default: true
      },
      boxesPercentage:{
        type: String,
        default: 'shots'
      },
      //gameId for print one game Player xG Map
      gameId:{
        type:Number,
        default: 0
      },
      //gameIds for print number of games selected
      gameIds: {
        type: Array,
        required: false,
        default: () => []
      },
      gameName:{
        type: String,
        default: ""
      },
      fontFamily:{
        type: String,
        default: 'NeueMontreal'
      },
      minsPlayed: {
        type: Number,
        default: 0,
      },
      embed:{
        type: Boolean,
        default: false
      },
      showPenalties: {
        type: Boolean,
        default: false
      },
      showAllInfoInBoxes: {
        type: Boolean,
        default: true
      },
      teamId: {
        type: Number,
        default: 0
      },
      filterShotTypeText: {
        type: String,
        default: ''
      },
      filterShotStrategyText: {
        type: String,
        default: ''
      },
      filterBodyPartText: {
        type: String,
        default: ''
      },
      filterMinutesText: {
        type: String,
        default: ''
      },
      filterTimeText: {
        type: String,
        default: ''
      },
      filterMinutesRange: {
        type: String,
        default: ''
      },
      orientation: {
        type: String,
        default: 'horizontal'
      }
    },
    computed: {
      SVG_WIDTH(){
        return this.size ? this.size : 380;
      },
      PITCH_WIDTH(){
        if(this.margin){
          return this.SVG_WIDTH - this.margin.left - this.margin.right
        }else{
          return this.SVG_WIDTH;
        }
      },
      PITCH_HEIGHT() {
        return this.game ? this.PITCH_WIDTH * 0.67125 : this.PITCH_WIDTH * 0.755
      },
      isVertical() {
        return this.orientation === 'vertical';
      },
      isMobile() {
        return this.$vuetify.breakpoint.smAndDown || this.$vuetify.breakpoint.mobile;
      },
      SVG_RENDER_WIDTH() {
        return this.isVertical ? this.PITCH_HEIGHT : this.SVG_WIDTH;
      },
      SVG_RENDER_HEIGHT() {
        return this.isVertical ? this.SVG_WIDTH : this.PITCH_HEIGHT;
      },
      rootElementId(){
        return `pitchXG${this.id}`
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      },
      currentUserAreas(){
        let layoutId = this.$store.getters["auth/fieldLayout"]
        return _.chain(this.layouts).find(l => l.id === layoutId).get("areas").reduce((allAreas,a) => {
          allAreas.push(a);
          return allAreas;
        },[]).value();
      },
      layouts(){
        return this.$store.getters["general/fieldLayouts"];
      },
      totalPenalties() { 
        return this.shots.filter(d => d.penaltyGoal).length;
      },
      totalPenaltyGoals() {
        return this.shots.filter(d => d.penaltyGoal && d.type === "GOAL").length;
      }
    },
    data() {
      return {
        images: {},
        observer: null
      }
    },
    watch: {
      shots: { handler() { this.printD3(true); }, deep: true },
      boxesPercentage() { this.printD3(true); },
      orientation() { this.printD3(true); }
    },
    mounted() {
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      this.printD3();
    },
    methods: {
      async printD3(force = false){
        if(force){
          this.images = {};
        }

        const SVG_WIDTH = this.SVG_WIDTH;
        const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};
        const zeroMargin = {top: 0, right: 0, bottom: 0, left: 0};

        const PITCH_WIDTH = this.PITCH_WIDTH;
        const PITCH_HEIGHT = this.PITCH_HEIGHT;
        const renderW = this.isVertical ? PITCH_HEIGHT : PITCH_WIDTH;
        const renderH = this.isVertical ? PITCH_WIDTH : PITCH_HEIGHT;

        const textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';

        d3.selectAll('.tooltip-no-arrow').remove();

        d3.select(`#${this.rootElementId}`).select("svg").remove();

        const tooltip = d3
          .select("body")
          .append("div")
          .attr("display", "none")
          .attr("class", "tooltip-no-arrow tooltip-no-arrow--centered");

        let svg = d3.select(`#${this.rootElementId}`)
          .append("svg")
          .attr("width", this.isVertical ? PITCH_HEIGHT : SVG_WIDTH)
          .attr("height", this.isVertical ? SVG_WIDTH : (PITCH_HEIGHT + margin.top + margin.bottom))
          .style("background-color", "#ffffff")
          .attr("id",`${this.rootElementId}SVG`)
          .attr("class", "pitch");

        const pitchLayer = svg.append("g").attr("transform",
          this.isVertical
            ? `translate(${margin.left},${margin.top + PITCH_WIDTH}) rotate(-90)`
            : `translate(${margin.left},${margin.top})`
        );
        const uiLayer = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

        this.drawImageBase64(pitchLayer, 0, 0, PITCH_WIDTH, PITCH_HEIGHT, require(`@/assets/img/${this.game ? "": "half_"}pitch_${this.currentUserFieldColor}.png`),"pitch",true);

        // Draw Driblab Logo (game mode: drawn below away team, same line as legend)
        if (!this.game) {
          const logoW = renderW * 0.17;
          const logoH = renderH * 0.09;
          this.drawImageBase64(uiLayer,
            renderW * 0.91 - logoW,
            renderH * 0.02 - 6,
            logoW,
            logoH,
            require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`),'logoDriblab');
        }

        let neutralColorsHome = getNeutralColors('home');
        let firstHomeTeamColor = this.homeFillColor;
        let secondHomeTeamColor = this.homeBorderColor;
        let neutralColorsAway = getNeutralColors('away');
        let firstAwayTeamColor =  this.awayFillColor;
        let secondAwayTeamColor = this.awayBorderColor;
        //If is game xG Map but it's post game, don't get team colors, keep neutral colors
        if (this.game && !this.mediaMode && !this.postGameAnalysis) {
          firstHomeTeamColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor : (this.game.homeTeamColor ? this.game.homeTeamColor : firstHomeTeamColor);
          secondHomeTeamColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor : (this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor : secondHomeTeamColor);
          firstAwayTeamColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor : (this.game.awayTeamColor ? this.game.awayTeamColor : firstAwayTeamColor);
          secondAwayTeamColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor : (this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor : secondAwayTeamColor);
        }
        if(this.game && firstHomeTeamColor === firstAwayTeamColor){
          firstAwayTeamColor = neutralColorsAway[0];
        }
        if(this.game && secondHomeTeamColor === secondAwayTeamColor){
          secondAwayTeamColor = neutralColorsAway[1];
        }

        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("boxes"))
          this.renderBoxes(pitchLayer,
            PITCH_WIDTH,
            PITCH_HEIGHT,
            zeroMargin,
            this.shots.filter(p => !p.ownGoal),
            this.language
          )

        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("circles"))
          this.renderShots(pitchLayer,
            PITCH_WIDTH,
            PITCH_HEIGHT,
            zeroMargin,
            this.rootElementId,
            this.shots.filter(p => !p.ownGoal),
            this.xGOnTarget,
            this.playerPosition ? this.playerPosition : "none",
            firstHomeTeamColor.replace("0x", "#"),
            secondHomeTeamColor.replace("0x", "#"),
            firstAwayTeamColor.replace("0x", "#"),
            secondAwayTeamColor.replace("0x", "#"),
            tooltip,
            this.game ? true : false);

        if (this.isVertical && this.circlesAndBoxes && this.circlesAndBoxes.includes("boxes")) {
          pitchLayer.selectAll("text").each(function() {
            const el = d3.select(this);
            const x = +el.attr("x") || 0;
            const y = +el.attr("y") || 0;
            el.attr("transform", `rotate(90, ${x}, ${y})`);
          });
        };

        if (this.game) {
          this.printGameXGLegend(uiLayer, renderW, renderH, zeroMargin, this.shots, this.game, this.language)
          this.printGameLegend(uiLayer, renderW, renderH, zeroMargin, firstHomeTeamColor, firstAwayTeamColor, this.game, this.shots)
          this.printGameBottomLegend(uiLayer, renderW, renderH, zeroMargin, this.shots, this.game)
          const rectW = renderW * 0.58;
          const rectH = renderW * 0.1;
          const rectX = renderW / 2 - rectW / 2;
          const rectY = renderH - rectH - renderW * 0.01;
          const gap = renderW * (this.isVertical ? 0.022 : 0.015);
          const legendW = renderW * (this.isVertical ? 0.16 : 0.12);
          const legendH = renderH * (this.isVertical ? 0.11 : 0.08);
          const legendY = rectY + rectH - legendH + renderW * (this.isVertical ? 0.04 : 0.008);
          this.drawImageBase64(uiLayer,
            rectX - legendW - gap,
            legendY,
            legendW,
            legendH,
            require(`@/assets/img/legend_xg${this.currentUserFieldColor === 'white' ? "_dark" : ""}_vertical.png`), 'legendPitchGame');

          this.writeText(svg, rectX - legendW/2 - (PITCH_WIDTH * 0.005), legendY + (PITCH_HEIGHT * (this.isVertical ? 0.065 : 0.021)), "middle", (0.008 * PITCH_WIDTH) + "px", this.$t('event.Goal').toUpperCase(), textColor);
          this.writeText(svg, rectX - legendW/2 + (PITCH_WIDTH * 0.015), legendY + legendH/2 + (PITCH_HEIGHT * 0.007), "middle", (0.008 * PITCH_WIDTH) + "px", this.$t('general.goalProbability').toUpperCase(), textColor);

          const logoW = PITCH_WIDTH * (this.isVertical ? 0.1 : 0.14);
          const logoH = PITCH_HEIGHT * 0.07;
          const logoY = rectY + rectH - logoH;
          this.drawImageBase64(uiLayer,
            rectX + rectW + gap,
            logoY,
            logoW,
            logoH,
            require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`), 'logoDriblab');
        }

        if (!this.game && this.game !== "") {
          this.printPlayerXGLegend(svg, PITCH_WIDTH, margin, this.shots, this.xGOnTarget, this.finishingXGOT, this.playerSeasonData, this.playerName, this.playerPosition ? this.playerPosition : "none",this.language)
          // Horizontal layout: GOAL to the right of GOAL PROBABILITY, smaller, aligned to pitch (player/team map)
          this.drawImageBase64(svg, margin.left + PITCH_WIDTH * 0.08, margin.top + PITCH_WIDTH * 0.018, PITCH_WIDTH * 0.28, PITCH_HEIGHT * 0.06, 
          require(`@/assets/img/legend_xg${this.currentUserFieldColor === 'white' ? "_dark" : ""}.png`), 'legendPitchGame');
          this.writeText(svg, margin.left + PITCH_WIDTH * 0.215, margin.top + PITCH_HEIGHT * 0.045, "middle", (0.012 * PITCH_WIDTH) + "px", this.$t('general.goalProbability').toUpperCase(), textColor);
          this.writeText(svg, margin.left + PITCH_WIDTH * 0.345, margin.top + PITCH_HEIGHT * 0.045, "middle", (0.012 * PITCH_WIDTH) + "px", this.$t('event.Goal').toUpperCase(), textColor);
          
        }
        let font = await loadFonts(this.fontFamily);
        copyAndAppendElement(svg.node(),font.node(),0,0);

        //Emit when is loaded
        this.$emit("loaded", this.id);
      },
      getPlayersWithHighestXG(shots) {
        // Group players by name
        const sumXGShots = shots.reduce(function(acc, val){
          var o = acc.filter(function(obj){
              return obj.player === val.player;
          }).pop() || {player: val.player, xG: 0};

          o.xG += val.xG;
          const index = acc.findIndex(p => p.player === o.player)
          if (index === -1)
            acc.push(o);
          else
            acc[index] = o;
          return acc;
        },[]);
        // Sort, take out players with xG < 0.1 and return first three
        return sumXGShots.sort((a, b) => b.xG - a.xG).filter(p => p.xG >= 0.1).slice(0, 3);
      },
      measureText(string, fontSize = 10) {
        const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
      		0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
      		0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
      		0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
      		0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
      		0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
      		0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
      		0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
        const avg = 0.5293256578947368;

        return string
          .split('')
          .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
          .reduce((cur, acc) => acc + cur) * fontSize;
      },
      truncateText(text, fontSize, maxWidth) {
        if (this.measureText(text, fontSize) <= maxWidth) return text;
        let t = text;
        while (this.measureText(t + '…', fontSize) > maxWidth && t.length > 1) {
          t = t.slice(0, -1);
        }
        return t + '…';
      },
      /** Final clamp for season lines: keep trailing " +N" (extra competitions) visible. */
      truncateSeasonLegendLine(line, fontSize, maxWidth) {
        const m = typeof line === 'string' && line.match(/^(.*)(\s\+\d+)$/);
        if (m && m[1].trim().length > 0) {
          const suffix = m[2];
          const mainTrim = m[1].replace(/\s+$/, '');
          const suffixW = this.measureText(suffix, fontSize);
          const maxMain = Math.max(0, maxWidth - suffixW);
          const main = this.truncateText(mainTrim, fontSize, maxMain);
          return main + suffix;
        }
        return this.truncateText(line, fontSize, maxWidth);
      },
      drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
        if(!this.images[idName]){
          let base_image = new Image();
          base_image.crossOrigin = "anonymous";
          base_image.src = img;
          base_image.onload = (event) => {
            const dataUrl = getDataUrl(event.currentTarget);
            this.drawImage(
            svg,
            x,
            y,
            width,
            height,
            dataUrl,
            idName,
            bottom
            );
          }
        }else{
          if(bottom){
            svg.node().insertBefore(this.images[idName],svg.node().firstChild);
          }else{
            svg.node().appendChild(this.images[idName]);
          }
        }
      },
      /** Same as drawImageBase64 but resolves after remote images are inlined (needed for PNG/SVG export). */
      drawImageBase64Async(svg, x, y, width, height, img, idName, bottom = false) {
        return new Promise((resolve) => {
          if (this.images[idName]) {
            if (bottom) {
              svg.node().insertBefore(this.images[idName], svg.node().firstChild);
            } else {
              svg.node().appendChild(this.images[idName]);
            }
            resolve();
            return;
          }
          const base_image = new Image();
          base_image.crossOrigin = "anonymous";
          base_image.onload = (event) => {
            const dataUrl = getDataUrl(event.currentTarget);
            this.drawImage(svg, x, y, width, height, dataUrl, idName, bottom);
            resolve();
          };
          base_image.onerror = () => resolve();
          base_image.src = img;
        });
      },
      drawImage: function(svg, x, y, width, height, img,idName,bottom = false) {
        let imgNode = bottom ? svg.insert("svg:image",":first-child") : svg.append("svg:image");
          imgNode.attr("id", idName)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
      this.images[idName] = imgNode.node().cloneNode(true);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className) {
        return svg.append("rect")
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
        svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", anchor)
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .attr("class", className)
          .style("font-size", fontSize)
          .style("font-family", this.fontFamily)
          .style("fill", fill)
          .text(text);
      },
      getTypeOfShot(name) {
      	if (name === "SAVED SHOT") return "Saved Shot";
      	if (name === "GOAL") return "Goal";
      	if (name === "MISSED SHOT") return "Missed Shot";
      	if (name === "SHOT ON POST") return "Shot on Post";
        if (name === "MISSED CHANCE") return "Chance Missed";
        if (name === "CHANCE MISSED") return "Chance Missed";
        if (name === "Regular play") return "Regular Play";
        if (name && name.startsWith("Individual")) return "Individual Play";
        if (name === "Fast break") return "Fast Break";
        if (name === "From corner") return "From Corner";
        if (name === "Free kick") return "Free Kick";
        if (name === "Set piece") return "Set Piece";
        if (name && name.startsWith("Throw")) return "Throw In";
        if (name === "Left footed") return "Left Foot";
        if (name === "Right footed") return "Right Foot";
        if (name === "Head") return "Head";
        if (name === "Other body part") return "Other Body Part";
        if (name === "Keeper Throw") return "Keeper Throw";
        if (name === "Penalty") return "Penalty";
        console.log("Unkown " + name);
      	return "??";
      },
      renderShots(svg, pitchSize, pitchHeight, margin, elementId, xgShots, xGOnTargetOption, playerPosition, teamColor, strokeColor, awayTeamColor, awayStrokeColor, tooltip, isGameXG = true) {
        if(this.currentUserFieldColor === 'white'){
          if(isWhite(teamColor)){
            teamColor = "#464646"
          }
          if(isWhite(awayTeamColor)){
            awayTeamColor = "#464646"
          }
        }

        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([pitchSize + margin.left, margin.left]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([pitchHeight + margin.top, margin.top]);

        let radius = d3.scale.linear().domain([0, 1]).range([pitchSize * 0.0055, pitchSize * 0.024]);

        var self = this;
        svg.selectAll("circle")
          .data(xgShots)
          .enter()
          .append("circle")
          .attr("r", d => playerPosition === 1 ? radius(d.xG) / 1.5 : radius(d.xG))
          .attr("cx", d => isGameXG ? d.homeTeam ? x(d.x) : x(100 - d.x) : x(d.y))
          .attr("cy", d => isGameXG ? d.homeTeam ? y(100 - d.y) : y(d.y) : y(d.ownGoal ? 100 - d.x : 100 - (100 - d.x) * 2))
          .attr('id', d => "shot" + d.id + "-" + elementId)
          .attr("class", d => "shot-" + d.playerId + " type-" + self.getTypeOfShot(d.type).split(" ").join("-") + " type-" +
              self.getTypeOfShot(d.strategy).split(" ").join("-") + " type-" + self.getTypeOfShot(d.bodyPart).split(" ").join("-") + " season-" + d.seasonId)
          .style("fill-opacity", (d) => self.getTypeOfShot(d.type) === "Goal" ? 0.90 : 0.25)
          .style("opacity", 0.95)
          .style("stroke", d => d.homeTeam ? strokeColor : awayStrokeColor)
          .style("stroke-width", playerPosition === 1 ? pitchSize * 0.002 / 1.5 : pitchSize * 0.0015)
          .style("fill", d => d.homeTeam ? teamColor : awayTeamColor)
          .on('mouseenter',function(xgShot) {
            d3.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', playerPosition === 1 ? radius(xgShot.xG) * 3.5 / 3 : radius(xgShot.xG) * 1.75);
            var statName = playerPosition === 1 ? 'xGOT' : 'xG';
            var shotId = document.getElementById("shot" + xgShot.id + "-" + elementId);
            var textLine1 = isGameXG ? xgShot.player : xgShot.game;
            var textLine2 = !isGameXG && playerPosition === 1 ? xgShot.player + " · " + xgShot.xG.toFixed(2) + " " + statName : xgShot.xG.toFixed(2) + " " + statName; // For GK we add shooter name to tooltip
            var textLine3 = self.getTypeOfShot(xgShot.type) + " at " + xgShot.min + ":" + (xgShot.sec < 10 ? "0" + xgShot.sec : xgShot.sec);
            var maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14), self.measureText(textLine3, 14));
            var widthTooltip = maxTextWidth + 60;
            var height = self.team ? 90 : 70;
            var topOffset = 90;
            var leftOffset = Math.ceil(shotId.getAttribute('r'));
            var tooltipTop = shotId.getBoundingClientRect().bottom + 10;
            tooltipTop = isGameXG && ((xgShot.y <= 50 && xgShot.homeTeam) || (!xgShot.homeTeam && xgShot.y >= 50)) ?
            shotId.getBoundingClientRect().top - topOffset + 10 : tooltipTop

            let tooltipLeft = self.$vuetify.breakpoint.mobile && !self.embed ? (window.innerWidth/2) - (widthTooltip/2) : shotId.getBoundingClientRect().left - (widthTooltip/2) + leftOffset;
            if(self.$vuetify.breakpoint.mobile && !self.embed){
              tooltipTop = d3.select(`#${self.rootElementId}`).node().getBoundingClientRect().top + window.scrollY + (height/2);
            }

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("width", widthTooltip + "px")
              .style("height", height + "px")
              .style("font-size", "14px")
              .style("left", (tooltipLeft) + "px")
              .style("top", (tooltipTop) + "px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html("<b>" + (isGameXG ? xgShot.player : xgShot.game)
              + "</b></br>"
              + (self.team ? `${xgShot.player}</br>` : '')
              + (!isGameXG && playerPosition === 1 ? xgShot.player + " · " : "")
              + "<b>" + xgShot.xG.toFixed(2) + " "
              + statName
              + (xGOnTargetOption && xgShot.xgOnTarget > 0.01 ? ' · ' + xgShot.xgOnTarget.toFixed(2) + ' xG OT' : '')
              + "</b></br>" + self.getTypeOfShot(xgShot.type)
              + " at " + xgShot.min + ":"
              + (xgShot.sec < 10 ? "0" + xgShot.sec : xgShot.sec));

            let colorLine = xgShot.homeTeam ? teamColor : awayTeamColor
            colorLine = isGameXG ? colorLine : 'white';

            let xEndShot = xgShot.x1;

            if(xgShot.type === "MISSED SHOT"
              && xgShot.y1 >= 44.2 && xgShot.y1 <= 55.8
              && xgShot.x1 && xgShot.x1 >= 100){
              xEndShot = 104;
            }

            if (self.getTypeOfShot(xgShot.type) !== "Chance Missed") {
              let hoverRadius = playerPosition === 1 ? radius(xgShot.xG) * 3.5 / 3 : radius(xgShot.xG) * 1.75;
              self.drawTrajectoryLine(svg, x, y, xgShot.homeTeam, !isGameXG,xgShot.ownGoal ? 100 - xgShot.x : xgShot.x, xgShot.y, !xEndShot || (xgShot.type === "GOAL" && xEndShot < 100) ? 100 : xEndShot, xgShot.y1, colorLine, hoverRadius);
            }

            if(xgShot.type === "GOAL"){
              self.drawGoalCircle(svg, x, y, pitchSize, isGameXG, xgShot.homeTeam, xgShot.id, !xgShot.x1 || xgShot.x1 < 100 ? 100 : xgShot.x1, xgShot.y1, "black", "green");
            }else if(xgShot.type !== "GOAL" && self.getTypeOfShot(xgShot.type) !== "Chance Missed"){
              self.drawFailCross(svg, x, y, pitchSize, isGameXG, xgShot.homeTeam, !xEndShot ? 100 : xEndShot,xgShot.y1, "red");
            }
          })
          .on('mouseout',function(xgShot) {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('r', d => playerPosition === 1 ? radius(d.xG) / 1.5 : radius(d.xG));
            d3.select("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".trajectory-line").remove();
            svg.selectAll(".goal-trajectory-circle").remove();
            svg.selectAll(".fail-cross-line").remove();
          })
          .on("click",(xgShot,e) => {
            if(this.game){
              window.open(`/game/${this.game.gameId}/video-highlights?d=${xgShot.teamId},${xgShot.min},${xgShot.sec},${xgShot.eventTypeId}`,'_blank');
            }
          })
          .transition()
          .duration(1000)
      },
      printGameXGLegend(svg, pitchWidth, pitchHeight, margin, shots, gameData, language = "en") {
        let homeShots = shots.filter(s => s.homeTeam);
        let awayShots = shots.filter(s => !s.homeTeam);
        let penaltyHomeException = homeShots.some(d => d.penaltyGoal);
        let penaltyAwayException = awayShots.some(d => d.penaltyGoal);
        let ownGoalHomeException = homeShots.some(d => d.ownGoal);
        let ownGoalAwayException = awayShots.some(d => d.ownGoal);

        let homeShotsNoOwnGoals = shots.filter(p => !p.ownGoal).filter(s => s.homeTeam);
        let awayShotsNoOwnGoals = shots.filter(p => !p.ownGoal).filter(s => !s.homeTeam);

        let homeGoals = homeShotsNoOwnGoals.length === 0 ? 0 : homeShotsNoOwnGoals.filter(d => d.type === "GOAL").length;
        let awayGoals = awayShotsNoOwnGoals.length === 0 ? 0 : awayShotsNoOwnGoals.filter(d => d.type === "GOAL").length;

        let homeXG = homeShotsNoOwnGoals.map(shot => shot.xG).reduce((a, b) => a + b, 0);
        let awayXG = awayShotsNoOwnGoals.map(shot => shot.xG).reduce((a, b) => a + b, 0);

        const rectangleSize = 0.28 * pitchWidth;
        const homeDiff = (homeGoals - homeXG).toFixed(1);
        const awayDiff = (awayGoals - awayXG).toFixed(1);
        const homeDiffStr = `(${homeGoals > homeXG ? '+' : ''}${homeDiff})`;
        const awayDiffStr = `(${awayGoals > awayXG ? '+' : ''}${awayDiff})`;

        this.drawRectangle(svg,
          (pitchWidth / 2 - rectangleSize / 2) + margin.left,
          margin.top,
          rectangleSize,
          rectangleSize * 0.28,
          "black",
          0.85,
          "xgLegendRect");
        this.writeText(svg, pitchWidth / 2 + margin.left, 0.008 * pitchWidth + margin.top, "middle", (0.018 * pitchWidth) + "px", Dictionary[this.language]["finishingMap"]["chart"]["sumOfxG"], "white");
        const valY = 0.032 * pitchWidth + margin.top;
        const valFont = (0.035 * pitchWidth) + "px";
        const diffFont = (0.016 * pitchWidth) + "px";
        const cx = pitchWidth / 2 + margin.left;
        const hasHomeStar = penaltyHomeException || ownGoalAwayException;
        const hasAwayStar = penaltyAwayException || ownGoalHomeException;
        this.writeText(svg, cx, valY, "middle", valFont,
          `${homeXG.toFixed(1)}${hasHomeStar ? '*' : ''} · ${awayXG.toFixed(1)}${hasAwayStar ? '*' : ''}`, "white");
        const baseDiffOffset = 0.065 * pitchWidth;
        const starExtra = 0.012 * pitchWidth;
        this.writeText(svg, cx - baseDiffOffset - (hasHomeStar ? starExtra : 0), valY, "end", diffFont, homeDiffStr, homeGoals > homeXG ? "green" : "red");
        this.writeText(svg, cx + baseDiffOffset + (hasAwayStar ? starExtra : 0), valY, "start", diffFont, awayDiffStr, awayGoals > awayXG ? "green" : "red");

      },
      getDistinctPlayerLegendTeamIds(seasons) {
        if (this.team) {
          const id = this.teamId || (seasons && seasons.length > 0 ? seasons[0].teamId : 0);
          return id ? [id] : [];
        }
        const seen = new Set();
        const ordered = [];
        for (const s of seasons || []) {
          const tid = s && s.teamId;
          if (tid && !seen.has(tid)) {
            seen.add(tid);
            ordered.push(tid);
            if (ordered.length >= 3) break;
          }
        }
        return ordered;
      },
      async printPlayerXGLegend(svg, pitchWidth, margin, shots, xgOnTargetOption, finishingXGOTOption, seasons, playerName,playerPosition, language = "en") {
        //Remove Penalty and own goals to count goals and xG
        let availableShots = shots.filter(p => !p.ownGoal); //|| this.team
        const xG = availableShots.map(shot => shot.xG).reduce((a, b) => a + b, 0);
        const xGOnTarget = availableShots.map(shot => shot.xgOnTarget).reduce((a, b) => a + b, 0);
        const finishingXGOT = availableShots.map(shot => shot.finishingXGOT).reduce((a, b) => a + b, 0);
        const shotsOnTarget = availableShots.filter(shot => shot.type === "GOAL").length + availableShots.filter(shot => shot.type === "SAVED SHOT" && !shot.blockedByOutfielderPlayer).length;
        const playerShots = availableShots.length;
        const goals = availableShots.length === 0 ? 0 : availableShots.filter(d => d.type === "GOAL").length;
        //If games selected, use that count; if none selected (gameIds empty), show 0; else use season totals
        const noGamesSelected = this.gameIds && this.gameIds.length === 0;
        const games = noGamesSelected ? 0 : (this.gameIds.length ? this.gameIds.length : seasons.map(s => s.gamesPlayed).reduce((a, b) => a + b, 0));
        const minutes = noGamesSelected ? 0 : (this.minsPlayed > 0 ? this.minsPlayed : seasons.map(s => s.minutesPlayed).reduce((a, b) => a + b, 0));
        let xGDecimals = 1;

        const rectW = 0.72 * pitchWidth;
        const rectH = 0.168 * pitchWidth;
        const rectX = (pitchWidth / 2 - rectW / 2) + margin.left;
        const rectY = pitchWidth * 0.577 + margin.top;

        this.drawRectangle(svg, rectX, rectY, rectW, rectH, "#1a1a1a", 0.92, "xgPlayerLegendRect");

        const statName = playerPosition === 1 ? 'xGOT' : (this.teamOrOpponent ? Dictionary[this.language]["playerScout"]["xG"] : Dictionary[this.language]["statsNaming"]["xGAgainst"]);
        const goalsLabel = this.teamOrOpponent ? Dictionary[this.language]["table"]["roster"]["goals"] : Dictionary[this.language]["playerScout"]["goalsConceded"];

        const crestTeamIds = this.getDistinctPlayerLegendTeamIds(seasons);
        const baseCrestSize = pitchWidth * 0.054;
        const nameGap = pitchWidth * 0.014;
        const baseNameFontSize = 0.026 * pitchWidth;
        const nameWidth = this.measureText(playerName || "", baseNameFontSize);
        const adjustedNameFontSize = baseNameFontSize;

        const isConceded = !this.teamOrOpponent;
        // --- SEASON DISPLAY MODE: toggle between 'two-line' and 'one-line' ---
        const seasonDisplayMode = this.team ? 'one-line' : 'two-line';
        const hasSecondSeasonLine = seasonDisplayMode === 'two-line' && !(this.gameId > 0 || this.gameName) && seasons.length >= 2;
        const singleCrestSize = hasSecondSeasonLine ? pitchWidth * 0.068 : baseCrestSize;

        let crestUnitSize;
        let crestClusterWidth;
        let crestStep = 0;
        if (crestTeamIds.length <= 1) {
          crestUnitSize = singleCrestSize;
          crestClusterWidth = crestUnitSize;
        } else {
          const overlapRatio = crestTeamIds.length === 2 ? 0.32 : 0.28;
          crestUnitSize = singleCrestSize * (crestTeamIds.length === 2 ? 0.84 : 0.74);
          crestStep = crestUnitSize * (1 - overlapRatio);
          crestClusterWidth = crestUnitSize + (crestTeamIds.length - 1) * crestStep;
        }

        const heroFontSizeCalc = pitchWidth * 0.025;
        const goalsBaseForCalc = Dictionary[this.language]["table"]["roster"]["goals"];
        const xgBaseForCalc = Dictionary[this.language]["playerScout"]["xG"];
        // Divider layout: core line + most of the xG OT suffix (blend avoids rule jumping all the way
        // right while still clearing the rendered hero, which is wider than measureText suggests).
        let heroTextCalcForDivider = `${ goals } ${ goalsBaseForCalc }`;
        if (this.showPenalties && this.totalPenaltyGoals > 0) heroTextCalcForDivider += ` (${this.totalPenaltyGoals} P)`;
        heroTextCalcForDivider += `  ·  ${ xG.toFixed(xGDecimals) } ${ xgBaseForCalc }`;
        const heroWidthBase = this.measureText(heroTextCalcForDivider, heroFontSizeCalc);
        let heroWidthCalc = heroWidthBase;
        if (xgOnTargetOption) {
          const heroWithOt = `${heroTextCalcForDivider}  ·  ${xGOnTarget.toFixed(xGDecimals)} xG OT`;
          const heroFullW = this.measureText(heroWithOt, heroFontSizeCalc);
          const otExtra = Math.max(0, heroFullW - heroWidthBase);
          heroWidthCalc = heroWidthBase + otExtra * 0.70 + pitchWidth * 0.014;
        }
        const finFontSize = pitchWidth * 0.013;
        const finTextCalc = finishingXGOTOption && shotsOnTarget > 0
          ? `${ (finishingXGOT / shotsOnTarget) > 1 ? '+' : ''}${ (((finishingXGOT / shotsOnTarget) - 1) * 100).toFixed(1) }% ${ Dictionary[this.language]["statsNaming"]["Finishing"] } xG OT`
          : '';
        const finWidthCalc = finTextCalc ? this.measureText(finTextCalc, finFontSize) : 0;

        const rawNameLineW = crestClusterWidth + nameGap + nameWidth;
        const rawHeroLineW = Math.max(heroWidthCalc, finWidthCalc);
        const rawContentW = Math.max(rawNameLineW, rawHeroLineW);
        const minBoxInsetX = pitchWidth * 0.038;
        const minDividerX = rectX + rectW * 0.48;
        const maxDividerX = rectX + rectW * 0.588;
        const contentToDividerGap = pitchWidth * 0.044;
        const minDividerXFromInset = rectX + minBoxInsetX + rawContentW + pitchWidth * 0.028;
        const dividerX = Math.min(maxDividerX, Math.max(minDividerX, rectX + rawContentW + contentToDividerGap, minDividerXFromInset));

        const leftAvailable = dividerX - rectX;
        const symmetricLeftPad = (leftAvailable - rawContentW) / 2;
        const minInsetX = pitchWidth * 0.032;
        const minSingleCrestPad = pitchWidth * 0.056;
        const maxLeftPad = Math.max(0, leftAvailable - rawContentW - pitchWidth * 0.010);
        const targetPad = crestTeamIds.length > 1
          ? Math.max(minInsetX, symmetricLeftPad)
          : Math.max(minSingleCrestPad, symmetricLeftPad);
        const leftPad = Math.min(maxLeftPad, Math.max(minBoxInsetX, Math.min(targetPad, maxLeftPad)));

        const leftContentH = crestUnitSize + pitchWidth * 0.012 + pitchWidth * 0.030 + pitchWidth * 0.014 + (hasSecondSeasonLine ? pitchWidth * 0.010 : 0);
        const leftTopY = rectY + (rectH - leftContentH) / 2;
        const crestY = leftTopY;

        for (let idx = 0; idx < crestTeamIds.length; idx++) {
          const tid = crestTeamIds[idx];
          const cx = rectX + leftPad + idx * crestStep;
          await this.drawImageBase64Async(svg, cx, crestY, crestUnitSize, crestUnitSize,
            'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + tid + '.png', 'xgPlayerCrest-' + tid);
        }

        const nameX = rectX + leftPad + crestClusterWidth + nameGap;
        const nameY = crestY + pitchWidth * 0.004;

        svg.append("text")
          .attr("x", nameX)
          .attr("y", nameY)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 600)
          .style("font-size", adjustedNameFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .attr("class", "playerXGName")
          .text(playerName);

        const seasonFontSize = pitchWidth * 0.015;
        const seasonLineH = pitchWidth * 0.016;
        const seasonDividerGap = pitchWidth * 0.030;
        const maxSeasonRaw = Math.max(0, dividerX - nameX - seasonDividerGap);
        const maxSeasonWidth = maxSeasonRaw * 0.92;
        let seasonLines = [];

        if (this.gameId > 0 || this.gameName) {
          seasonLines.push(this.truncateText(this.gameName || '', seasonFontSize, maxSeasonWidth));
          xGDecimals = 2;
        } else if (seasons.length > 0) {
          const names = seasons.map(s => s.seasonName);

          if (seasonDisplayMode === 'two-line') {
            // TWO-LINE: 2 seasons → one per row; 3+ → "Name1, Name2" then "Name3, Name4… +N"
            if (names.length === 1) {
              seasonLines.push(this.truncateText(names[0], seasonFontSize, maxSeasonWidth));
            } else if (names.length === 2) {
              seasonLines.push(this.truncateText(names[0], seasonFontSize, maxSeasonWidth));
              seasonLines.push(this.truncateText(names[1], seasonFontSize, maxSeasonWidth));
            } else {
              const prefix1 = names[0] + ', ';
              const prefixW1 = this.measureText(prefix1, seasonFontSize);
              const line1 = prefix1 + this.truncateText(names[1], seasonFontSize, maxSeasonWidth - prefixW1);
              seasonLines.push(line1);

              if (names.length >= 3) {
                let extra = '';
                if (names.length >= 4) {
                  // Align with one-line legend: +N = seasons beyond the first two (n−2), reserving width so truncation fits the container.
                  extra = ` +${names.length - 2}`;
                } else if (names.length === 3) {
                  const suffixOne = ' +1';
                  const wSuffix = this.measureText(suffixOne, seasonFontSize);
                  const w3 = this.measureText(names[2], seasonFontSize);
                  if (w3 + wSuffix > maxSeasonWidth * 0.86) {
                    extra = suffixOne;
                  }
                }
                const availW2 = extra ? maxSeasonWidth - this.measureText(extra, seasonFontSize) : maxSeasonWidth;
                if (names.length >= 4) {
                  const prefix2 = names[2] + ', ';
                  const prefixW2 = this.measureText(prefix2, seasonFontSize);
                  const line2 = prefix2 + this.truncateText(names[3], seasonFontSize, availW2 - prefixW2) + extra;
                  seasonLines.push(line2);
                } else {
                  const line2 = this.truncateText(names[2], seasonFontSize, availW2) + extra;
                  seasonLines.push(line2);
                }
              }
            }
          } else {
            // ONE-LINE: "Name1, Name2… +N" all on a single line
            const extraCount = names.length > 2 ? ` +${names.length - 2}` : '';
            const maxNames = Math.min(names.length, 2);
            let joined = names.slice(0, maxNames).join(', ');
            let seasonText = joined + extraCount;

            let seasonWidth = this.measureText(seasonText, seasonFontSize);
            if (seasonWidth > maxSeasonWidth && maxNames > 1) {
              const newExtra = ` +${names.length - 1}`;
              seasonText = names[0] + newExtra;
              seasonWidth = this.measureText(seasonText, seasonFontSize);
            }
            if (seasonWidth > maxSeasonWidth) {
              seasonText = this.truncateText(seasonText, seasonFontSize, maxSeasonWidth);
            }
            seasonLines.push(seasonText);
          }
        }

        seasonLines = seasonLines.map(line => this.truncateSeasonLegendLine(line, seasonFontSize, maxSeasonWidth));

        seasonLines.forEach((line, i) => {
          svg.append("text")
            .attr("x", nameX)
            .attr("y", nameY + pitchWidth * 0.030 + i * seasonLineH)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", seasonFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.85)")
            .attr("class", "seasonText")
            .text(line);
        });

        const heroFontSize = pitchWidth * 0.025;
        const heroY = crestY + crestUnitSize + pitchWidth * 0.008 + (hasSecondSeasonLine ? pitchWidth * 0.010 : 0);

        if (isConceded) {
          const goalsBaseLabel = Dictionary[this.language]["table"]["roster"]["goals"];
          const xgBaseLabel = Dictionary[this.language]["playerScout"]["xG"];
          const concededSuffix = goalsLabel.replace(goalsBaseLabel, '').trim() || 'Conceded';

          let goalsPart = `${ goals } ${ goalsBaseLabel }`;
          if (this.showPenalties && this.totalPenaltyGoals > 0) goalsPart += ` (${this.totalPenaltyGoals} P)`;
          const xgPart = `${ xG.toFixed(xGDecimals) } ${ xgBaseLabel }`;
          let heroLine1 = `${ goalsPart }  ·  ${ xgPart }`;
          if (xgOnTargetOption) heroLine1 += `  ·  ${xGOnTarget.toFixed(xGDecimals)} xG OT`;

          svg.append("text")
            .attr("x", rectX + leftPad)
            .attr("y", heroY)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 700)
            .style("font-size", heroFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .attr("class", "playerXGandGoals")
            .text(heroLine1);

          const concededFontSize = pitchWidth * 0.014;
          const goalsPartWidth = this.measureText(goalsPart, heroFontSize);
          const dotWidth = this.measureText('  ·  ', heroFontSize);

          svg.append("text")
            .attr("x", rectX + leftPad)
            .attr("y", heroY + pitchWidth * 0.027)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 500)
            .style("font-size", concededFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(concededSuffix);

          svg.append("text")
            .attr("x", rectX + leftPad + goalsPartWidth + dotWidth - pitchWidth * 0.030)
            .attr("y", heroY + pitchWidth * 0.027)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 500)
            .style("font-size", concededFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(concededSuffix);
        } else {
          let goalsText = `${ goals } ${ goalsLabel }`;
          if (this.showPenalties && this.totalPenaltyGoals > 0) goalsText += ` (${this.totalPenaltyGoals} P)`;
          let heroLine1 = `${ goalsText }  ·  ${ xG.toFixed(xGDecimals) } ${ statName }`;
          if (xgOnTargetOption) heroLine1 += `  ·  ${xGOnTarget.toFixed(xGDecimals)} xG OT`;

          svg.append("text")
            .attr("x", rectX + leftPad)
            .attr("y", heroY)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 700)
            .style("font-size", heroFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .attr("class", "playerXGandGoals")
            .text(heroLine1);
        }

        if (finishingXGOTOption && shotsOnTarget > 0) {
          const finText = `${ (finishingXGOT / shotsOnTarget) > 1 ? '+' : ''}${ (((finishingXGOT / shotsOnTarget) - 1) * 100).toFixed(1) }% ${ Dictionary[this.language]["statsNaming"]["Finishing"] } xG OT`;
          const finY = isConceded ? heroY + pitchWidth * 0.042 : heroY + pitchWidth * 0.028;
          svg.append("text")
            .attr("x", rectX + leftPad)
            .attr("y", finY)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 500)
            .style("font-size", (pitchWidth * 0.013) + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.9)")
            .text(finText);
        }

        svg.append("line")
          .attr("x1", dividerX)
          .attr("y1", rectY + pitchWidth * 0.022)
          .attr("x2", dividerX)
          .attr("y2", rectY + rectH - pitchWidth * 0.022)
          .attr("stroke", "rgba(255,255,255,0.5)")
          .attr("stroke-width", 1);

        const rightX = dividerX + pitchWidth * 0.048;
        const rightSectionW = rectX + rectW - dividerX;
        const rightPad = pitchWidth * 0.012;

        const labelFontSize = pitchWidth * 0.012;
        const valueFontSize = pitchWidth * 0.019;
        const rowH = pitchWidth * 0.042;
        const midColX = dividerX + rightSectionW * 0.5;
        const totalRows = 3;
        const rightContentH = totalRows * rowH;
        const startY = rectY + (rectH - rightContentH) / 2;

        const gamesLabel = this.team
          ? Dictionary[this.language]["table"]["history"]["games"]
          : Dictionary[this.language]["table"]["history"]["games"] + ' / ' + Dictionary[this.language]["table"]["roster"]["minutes"];
        const gamesValue = (games === 1 && this.gameName)
          ? this.gameName
          : (this.team ? `${games}` : `${games} / ${minutes}`);
        const statsData = [
          { label: gamesLabel, value: gamesValue },
          { label: Dictionary[this.language]["game"]["stats"]["shots"], value: `${playerShots}` },
          { label: Dictionary[this.language]["finishingMap"]["chart"]["xGPerShot"], value: `${playerShots > 0 ? (xG / playerShots).toFixed(2) : '0.00'}` }
        ];

        statsData.forEach((stat, i) => {
          const y = startY + i * rowH;
          svg.append("text")
            .attr("x", rightX)
            .attr("y", y)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", labelFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.7)")
            .text(stat.label);
          svg.append("text")
            .attr("x", rightX)
            .attr("y", y + pitchWidth * 0.015)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 600)
            .style("font-size", valueFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(stat.value);
        });

        const typesLabel = Dictionary[this.language]["finishingMap"]["typesOfShot"];
        const strategyLabel = Dictionary[this.language]["finishingMap"]["shotStrategy"];
        const bodyPartLabel = Dictionary[this.language]["finishingMap"]["bodyPart"];
        const allLabel = Dictionary[this.language]["general"] && Dictionary[this.language]["general"]["all"] ? Dictionary[this.language]["general"]["all"] : 'All';
        const maxFilterWidth = rectX + rectW - midColX - pitchWidth * 0.034;
        const filtersData = [
          { label: typesLabel, value: this.filterShotTypeText || allLabel },
          { label: strategyLabel, value: this.filterShotStrategyText || allLabel },
          { label: bodyPartLabel, value: this.filterBodyPartText || allLabel }
        ];

        filtersData.forEach((filter, i) => {
          const y = startY + i * rowH;
          svg.append("text")
            .attr("x", midColX)
            .attr("y", y)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", labelFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.7)")
            .text(filter.label);

          const truncatedValue = this.truncateText(filter.value, valueFontSize, maxFilterWidth);
          svg.append("text")
            .attr("x", midColX)
            .attr("y", y + pitchWidth * 0.018)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 600)
            .style("font-size", valueFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(truncatedValue);
        });

        const bottomInfoY = rectY + rectH - pitchWidth * 0.032;
        const bottomInfoFontSize = pitchWidth * 0.011;

        if (!this.showPenalties) {
          svg.append("text")
            .attr("x", rectX + pitchWidth * 0.038)
            .attr("y", bottomInfoY)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", bottomInfoFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text('*' + Dictionary[this.language]["finishingMap"]["chart"]["penaltiesExcluded"]);
        }

        const minutesRangeText = this.filterMinutesText || this.filterMinutesRange;
        if (minutesRangeText) {
          svg.append("text")
            .attr("x", rectX + rectW - pitchWidth * 0.038)
            .attr("y", bottomInfoY)
            .attr("text-anchor", "end")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", bottomInfoFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(minutesRangeText);
        }

      },
      printGameBottomLegend(svg, pitchWidth, pitchHeight, margin, shots, gameData) {
        const homeShots = shots.filter(s => s.homeTeam);
        const awayShots = shots.filter(s => !s.homeTeam);
        const hasOwnGoals = shots.filter(s => s.ownGoal).length > 0;
        const homeShotsNoOG = homeShots.filter(s => !s.ownGoal); //.filter(s => s.homeTeam);
        const awayShotsNoOG = awayShots.filter(s => !s.ownGoal); //.filter(s => !s.homeTeam);

        const homePenalties = homeShots.filter(d => d.penaltyGoal).length;
        const awayPenalties = awayShots.filter(d => d.penaltyGoal).length;
        
        const homeXG = homeShotsNoOG.map(s => s.xG).reduce((a, b) => a + b, 0);
        const awayXG = awayShotsNoOG.map(s => s.xG).reduce((a, b) => a + b, 0);
        const homeShotCount = homeShotsNoOG.length;
        const awayShotCount = awayShotsNoOG.length;
     
        const homeXGPerShot = homeShotCount > 0 ? (homeXG / homeShotCount) : 0;
        const awayXGPerShot = awayShotCount > 0 ? (awayXG / awayShotCount) : 0;
        
        const homeXGPlayers = this.getPlayersWithHighestXG(homeShotsNoOG);
        const awayXGPlayers = this.getPlayersWithHighestXG(awayShotsNoOG);

        const rectW = pitchWidth * 0.58;
        const rectH = pitchWidth * (hasOwnGoals ? 0.11 : 0.1);
        const rectX = (pitchWidth / 2 - rectW / 2) + margin.left;
        const rectY = pitchHeight + margin.top - rectH - pitchWidth * 0.01;

        this.drawRectangle(svg, rectX, rectY, rectW, rectH, "#1a1a1a", 0.92, "gameBottomRect");

        const colPad = pitchWidth * 0.012;
        const rightColEnd = rectX + rectW - colPad;
        const centerX = rectX + rectW / 2;

        const vDivLeftX = centerX - pitchWidth * 0.12;
        const vDivRightX = centerX + pitchWidth * 0.12;

        const playerFont = (pitchWidth * 0.012) + "px";
        const topY = rectY + pitchWidth * 0.01;
        const playerNameW = pitchWidth * 0.1;
        const playerXGOffset = pitchWidth * 0.13;

        const homePlayerStart = vDivLeftX - playerXGOffset - pitchWidth * 0.015;
        const awayPlayerStart = vDivRightX + pitchWidth * 0.015;

        // --- Home players (left, no title) ---
        homeXGPlayers.forEach((p, i) => {
          const y = topY + i * pitchWidth * 0.017;
          this.writeText(svg, homePlayerStart, y, "start", playerFont, this.truncateText(p.player, pitchWidth * 0.012, playerNameW), "white");
          this.writeText(svg, homePlayerStart + playerXGOffset, y, "end", playerFont, p.xG.toFixed(2), "rgba(255,255,255,0.8)");
        });

        // --- Away players (right, no title) ---
        awayXGPlayers.forEach((p, i) => {
          const y = topY + i * pitchWidth * 0.017;
          this.writeText(svg, awayPlayerStart, y, "start", playerFont, this.truncateText(p.player, pitchWidth * 0.012, playerNameW), "white");
          this.writeText(svg, awayPlayerStart + playerXGOffset, y, "end", playerFont, p.xG.toFixed(2), "rgba(255,255,255,0.8)");
        });

        // --- Vertical dividers ---
        const vDivTop = rectY + pitchWidth * 0.006;
        const vDivBottom = topY + 3 * pitchWidth * (hasOwnGoals ? 0.02 : 0.017);
        svg.append("line")
          .attr("x1", vDivLeftX).attr("y1", vDivTop)
          .attr("x2", vDivLeftX).attr("y2", vDivBottom)
          .attr("stroke", "rgba(255,255,255,0.25)").attr("stroke-width", 1);
        svg.append("line")
          .attr("x1", vDivRightX).attr("y1", vDivTop)
          .attr("x2", vDivRightX).attr("y2", vDivBottom)
          .attr("stroke", "rgba(255,255,255,0.25)").attr("stroke-width", 1);

        // --- CENTER: Shots + xG per Shot ---
        const statLabelFont = (pitchWidth * 0.012) + "px";
        const statValueFont = (pitchWidth * 0.018) + "px";

        const shotsLabel = Dictionary[this.language]["finishingMap"]["chart"]["shots"];
        const xgPerShotLabel = Dictionary[this.language]["finishingMap"]["chart"]["xGPerShot"];

        const homeValX = centerX - pitchWidth * 0.055;
        const awayValX = centerX + pitchWidth * 0.055;

        const shotsY = topY;
        this.writeText(svg, homeValX, shotsY, "end", statValueFont,
          `${homeShotCount}${homePenalties ? ' (' + homePenalties + 'P)' : ''}`, "white");
        this.writeText(svg, centerX, shotsY + pitchWidth * 0.003, "middle", statLabelFont, shotsLabel, "rgba(255,255,255,0.7)");
        this.writeText(svg, awayValX, shotsY, "start", statValueFont,
          `${awayShotCount}${awayPenalties ? ' (' + awayPenalties + 'P)' : ''}`, "white");

        const xgpsY = shotsY + pitchWidth * 0.025;
        this.writeText(svg, homeValX, xgpsY, "end", statValueFont, homeXGPerShot.toFixed(2), "white");
        this.writeText(svg, centerX, xgpsY + pitchWidth * 0.003, "middle", statLabelFont, xgPerShotLabel, "rgba(255,255,255,0.7)");
        this.writeText(svg, awayValX, xgpsY, "start", statValueFont, awayXGPerShot.toFixed(2), "white");
        
        if(hasOwnGoals){
          const ownGoalsMessageY = xgpsY + pitchWidth * 0.025; 
          this.writeText(svg, centerX, ownGoalsMessageY, "middle", statLabelFont, 
          Dictionary[this.language]["finishingMap"]["chart"]["exceptionTextOwnGoals"], "rgba(255,255,255,0.7)");
        }

        // --- Horizontal divider (full width) ---
        const hDivY = vDivBottom + pitchWidth * 0.004;
        svg.append("line")
          .attr("x1", rectX + colPad)
          .attr("y1", hDivY)
          .attr("x2", rectX + rectW - colPad)
          .attr("y2", hDivY)
          .attr("stroke", "rgba(255,255,255,0.25)")
          .attr("stroke-width", 1);

        // --- BOTTOM: Filter row (full width) ---
        const filterLabelFont = (pitchWidth * 0.01) + "px";
        const filterValueFont = (pitchWidth * 0.012) + "px";
        const filterY = hDivY + pitchWidth * 0.006;
        const allLabel = Dictionary[this.language]["general"] && Dictionary[this.language]["general"]["all"] ? Dictionary[this.language]["general"]["all"] : 'All';

        const typesLabel = Dictionary[this.language]["finishingMap"]["typesOfShot"];
        const strategyLabel = Dictionary[this.language]["finishingMap"]["shotStrategy"];
        const bodyPartLabel = Dictionary[this.language]["finishingMap"]["bodyPart"];

        const timeValue = this.filterTimeText || (Dictionary[this.language]["videoEvents"] && Dictionary[this.language]["videoEvents"]["fullGame"]) || 'Full Game';

        const filters = [
          { label: "Time", value: timeValue },
          { label: typesLabel, value: this.filterShotTypeText || allLabel },
          { label: strategyLabel, value: this.filterShotStrategyText || allLabel },
          { label: bodyPartLabel, value: this.filterBodyPartText || allLabel }
        ];

        const filterStartX = homePlayerStart;
        const filterTotalW = rightColEnd - filterStartX;
        const filterItemW = filterTotalW / filters.length;

        filters.forEach((f, i) => {
          const x = filterStartX + i * filterItemW;
          this.writeText(svg, x, filterY, "start", filterLabelFont, f.label, "rgba(255,255,255,0.6)");
          this.writeText(svg, x, filterY + pitchWidth * 0.012, "start", filterValueFont,
            this.truncateText(f.value, pitchWidth * 0.012, filterItemW * 0.9), "white");
        });

        const minutesRangeText = this.filterMinutesText || this.filterMinutesRange;
        if (minutesRangeText) {
          const minutesY = filterY + pitchWidth * 0.018 - 1;
          this.writeText(svg, rightColEnd, minutesY, "end", filterLabelFont, minutesRangeText, "white");
        }
      },
      printGameLegend(svg, pitchSize, pitchHeight, margin, homeTeamColor, awayTeamColor, gameData, shots, language = "en") {

        let homeScore = this.game.homeTeamScore;
        let awayScore = this.game.awayTeamScore;

        let secondHomeTeamColor = this.homeBorderColor;
        let neutralColorsAway = getNeutralColors('away');
        let secondAwayTeamColor = this.game ? neutralColorsAway[1] : this.awayBorderColor;
        
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        
        let homeTeamCrestXFactor;
        if (this.isVertical) {
          homeTeamCrestXFactor = this.isMobile ? 0.12 : 0.08;
        }
        else {
          homeTeamCrestXFactor = this.isMobile ? 0.09 : 0.07;
        }
        let homeCrestX = homeTeamCrestXFactor * pitchSize + margin.left

        let rectHome = this.drawRectangle(svg,
          homeCrestX - 16,
          margin.top - 8,
          16,
          (0.075 * pitchHeight) + 5,
          homeTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
        rectHome
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondHomeTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondHomeTeamColor}`)
        .attr("rx",3)
        .attr("ry",3)

        
        let homeTeamNameXFactor;
        if (this.isVertical) {
          homeTeamNameXFactor = this.isMobile ? 0.18 : 0.14;
        }
        else {
          homeTeamNameXFactor = this.isMobile ? 0.13 : 0.115;
        }

        this.drawImageBase64(svg,
          homeTeamCrestXFactor * pitchSize + margin.left,
          (this.isVertical ? 0.03 : 0.02) * pitchHeight + margin.top,
          0.05 * pitchSize,
          0.05 * pitchHeight,
          'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.homeTeamId + '.png','homeCrest');
        this.writeText(svg,
          homeTeamNameXFactor * pitchSize + margin.left,
          (this.isVertical ? 0.05 : 0.03) * pitchHeight + margin.top,
          "start",
          ((this.isVertical ? 0.024 : 0.022) * pitchSize) + "px",
          `${ this.homeTeamName } • ` + homeScore,textColor);

        let rectAway = this.drawRectangle(svg,
          0.935 * pitchSize + margin.left,
          margin.top - 8,
          16,
          (0.075 * pitchHeight) + 5,
          awayTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
        rectAway
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondAwayTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondAwayTeamColor}`)
        .attr("rx",3)
        .attr("ry",3)

        this.drawImageBase64(svg,
          (this.isVertical ? 0.875 : 0.885) * pitchSize + margin.left,
          (this.isVertical ? 0.03 : 0.02) * pitchHeight + margin.top,
          0.05 * pitchSize,
          0.05 * pitchHeight,
          'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.awayTeamId + '.png');
        this.writeText(svg,
          (this.isVertical ? 0.865 : 0.89) * pitchSize + margin.left,
          (this.isVertical ? 0.05 : 0.03) * pitchHeight + margin.top,
          "end",
          ((this.isVertical ? 0.024 : 0.022) * pitchSize) + "px",
          awayScore + ` • ${ this.awayTeamName }`,textColor);

      },
      renderBoxes(svg, svgWidth, pitchHeight, margin, shots, language = 'en') {
        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([this.PITCH_WIDTH, 0]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([this.PITCH_HEIGHT, 0]);

        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';

        const shotLegend = Dictionary[this.language]["statsNaming"]["Shots"].toLowerCase();

        var shadowScale = d3.scale.linear().domain([0, shots.length]).range([0.1, 0.75]);

        const xgTotal = _.reduce(shots,(accum,s) => accum += s.xG,0)

        _.forEach(this.currentUserAreas,(a,i) => {
          let areaEvents = _.filter(shots,(e) => {
            let target = e.startAreaId;
            let areaContain = a.id === target;
            return areaContain
          })
          let currentXG = _.reduce(areaEvents,(acccum,e) => { return acccum += e.xG;},0).toFixed(2)
          let pct = this.boxesPercentage === 'shots' ? Math.round(areaEvents.length / shots.length * 100) : Math.round(currentXG / xgTotal * 100);
          if(!_.isEmpty(areaEvents)){
            const verticalFactor = 2;
            let rectX = x(a.y2);
            let rectY = y(100 - (100 - a.x2) * verticalFactor);
            let rectWidth = x(a.y1) - x(a.y2);
            let rectHeight = (y(a.x1) - y(a.x2)) * verticalFactor;
  
            this.drawRectangle(svg,
                rectX,
                rectY,
                rectWidth,
                rectHeight, "red", shadowScale(areaEvents.length),`area-${i}-${a.id}`);
            
            let centerX = rectX + (rectWidth/2);
            let centerY = rectY + (rectHeight/2);
            let successful = _.filter(areaEvents,(e) => e.type === "GOAL");
  
            this.writeText(svg, centerX , centerY - (0.03 * this.PITCH_HEIGHT), "middle", (0.018 * svgWidth) + "px", `${ pct } %`,textColor);
            if (this.showAllInfoInBoxes) {
              this.writeText(svg, centerX , centerY, "middle", (0.018 * svgWidth) + "px",`(${ Math.round(areaEvents.length) } ${ shotLegend })`,textColor);
              this.writeText(svg, centerX , centerY + (0.03 * this.PITCH_HEIGHT), "middle", (0.018 * svgWidth) + "px",
              `(${currentXG} xG)`,textColor);
              this.writeText(svg, centerX , centerY + (0.06 * this.PITCH_HEIGHT), "middle", (0.018 * svgWidth) + "px",
              `${((successful.length / areaEvents.length) * 100).toFixed(0)}% ${ Dictionary[this.language]["statsNaming"]["Finishing"] }`,textColor);
            };
          }
        })
      },
      drawTrajectoryLine(svg,xScale,yScale,isHomeTeam, isPlayer, x0, y0, x1, y1, strokeColor = "black", circleRadius = 0) {
        let sx0;
        let sy0;
        let sx1;
        let sy1;
        if (isPlayer) {
          sx0 = xScale(y0);
          sy0 = yScale(100 - (100 - x0) * 2);
          sx1 = xScale(y1);
          sy1 = yScale(2 * x1 - 100);
        } else if (isHomeTeam) {
          sx0 = xScale(x0);
          sy0 = yScale(100 - y0);
          sx1 = xScale(x1);
          sy1 = yScale(100 - y1);
        } else {
          sx0 = xScale(100 - x0);
          sy0 = yScale(y0);
          sx1 = xScale(100 - x1);
          sy1 = yScale(y1);
        }

        // Offset line start by circle radius so the line begins at the circle edge
        if (circleRadius > 0) {
          let dx = sx1 - sx0;
          let dy = sy1 - sy0;
          let dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 0) {
            sx0 += (dx / dist) * circleRadius;
            sy0 += (dy / dist) * circleRadius;
          }
        }

        return svg.append("line")
          .attr("x1", sx0)
          .attr("y1", sy0)
          .attr("x2", sx1)
          .attr("y2", sy1)
          .attr("stroke-width", 1.5)
          .attr("class", "trajectory-line")
          .style("pointer-events", "none")
          .style("opacity", 1)
          .attr("stroke", strokeColor);
      },
      drawGoalCircle(svg, scaleX, scaleY, pitchSize, isGame, isHomeTeam, id, x, y, strokeColor, fillColor){
        svg
          .append("circle")
          .attr("r", d => 5)
          .attr("cx", d => isGame ? isHomeTeam ? scaleX(x) : scaleX(100 - x) : scaleX(y))
          .attr("cy", d => isGame ? isHomeTeam ? scaleY(100 - y) : scaleY(y) : scaleY(100 - (100 - x) * 2))
          .attr('id', d => "goal-trajectory-circle-" + id)
          .attr("class", d => "goal-trajectory-circle")
          .style("fill-opacity", (d) => 0.88)
          .style("opacity", 0.95)
          .style("stroke", d => strokeColor)
          .style("stroke-width", pitchSize * 0.0015)
          .style("fill", d => fillColor)
      },
      drawFailCross(svg, scaleX, scaleY, pitchSize, isGame, isHomeTeam, x, y, strokeColor){
        let offsetX = isGame ? 0.9 : 1.4;
        let offsetY = isGame ? 2 : 1;
        let sx0;
        let sy0;
        let sx1;
        let sy1;
        if (!isGame) {
          sx0 = scaleX(y + offsetY);
          sy0 = scaleY(100 - (100 - x - offsetX) * 2);
          sx1 = scaleX(y - offsetY);
          sy1 = scaleY(2 * x - 100 - offsetX);
        } else if (isHomeTeam) {
          sx0 = scaleX(x + offsetX);
          sy0 = scaleY(100 - y - offsetY);
          sx1 = scaleX(x - offsetX);
          sy1 = scaleY(100 - y + offsetY);
        } else {
          sx0 = scaleX(100 - x + offsetX);
          sy0 = scaleY(y - offsetY);
          sx1 = scaleX(100 - x - offsetX);
          sy1 = scaleY(y + offsetY);
        }

        svg.append('path')
        .attr("d", d3.svg.symbol().type('cross').size(pitchSize * 0.2))
        .attr('stroke', strokeColor)
        .attr('fill', strokeColor)
        .attr('stroke-width', 0.1)
        .attr("transform", function(d) { return "translate(" + ((sx0 + sx1) / 2 ) + "," + ((sy0 + sy1) / 2 ) + ") rotate(-45)"; })
        .attr("class", "fail-cross-line");

      },
      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      }
    },
    destroyed() {
      delete this.observer;
      d3.select(`#${this.rootElementId}`).select("svg").remove();
    },
  }

</script>


// WEBPACK FOOTER //
// src/views/base/XGChart.vue